import * as React from 'react';
import {
  Box,
  Checkbox,
  StackLayout,
  ParagraphText,
  StatusBadge,
  StatusBanner,
  UtilityText,
  Icon,
  TextAction,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import type { Nullable } from '@web-config-app/core';
import type { GDSStatus } from '@leagueplatform/genesis-core';
import { getEntityStatusBadgeProps } from '@web-config-app/core';
import { FullscreenConfirmationModal } from '../fullscreen-confirmation-modal/fullscreen-confirmation-modal.component';

export interface DeepPublishTreeNode {
  id: string;
  entityName: string;
  domainEntityType: string;
  status: string;
  link?: string;
  isDuplicate?: boolean;
  error?: string;
  childNodes: DeepPublishTreeNode[];
}

export interface DeepPublishConfirmationModalProps {
  isOpen: boolean;
  isLoading: boolean;
  rootNode?: Nullable<DeepPublishTreeNode>;
  errorMessage?: Nullable<string>;
  hasFailures?: boolean;
  publishableCount: number;
  publishDangerously?: boolean;
  environmentName?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export const getStatusBadge = (
  status: string,
): { genesisStatus: GDSStatus; labelKey: string } => {
  const badgeProps = getEntityStatusBadgeProps(status);
  return {
    genesisStatus: badgeProps.genesisStatus,
    labelKey: badgeProps.labelKey,
  };
};

export const DeepPublishTreeItem = ({
  node,
  duplicateIndicatorMessageId = 'DEEP_PUBLISH_DUPLICATE_INDICATOR',
}: {
  node: DeepPublishTreeNode;
  duplicateIndicatorMessageId?: string;
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = React.useState(true);
  const badgeProps = getStatusBadge(node.status);
  const badge = {
    genesisStatus: badgeProps.genesisStatus,
    label: formatMessage({ id: badgeProps.labelKey }),
  };
  const isError = Boolean(node.error);

  return (
    <StackLayout spacing="$half" css={{ paddingBlockEnd: '$half' }}>
      <StackLayout spacing="$quarter">
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="center"
        >
          <StatusBadge
            status={isError ? 'error' : badge.genesisStatus}
            label={isError ? 'Error' : badge.label}
          />
          {node.domainEntityType && (
            <UtilityText css={{ typography: '$overline' }}>
              {node.domainEntityType}
            </UtilityText>
          )}
          {node.link ? (
            <TextAction
              as={Link}
              to={node.link}
              target="_blank"
              rel="noopener noreferrer"
              indicator="externalLink"
            >
              {node.entityName}
            </TextAction>
          ) : (
            <UtilityText>{node.entityName}</UtilityText>
          )}
          {node.isDuplicate && (
            <UtilityText emphasis="subtle" css={{ fontStyle: 'italic' }}>
              {formatMessage({ id: duplicateIndicatorMessageId })}
            </UtilityText>
          )}
        </StackLayout>
        {isError && (
          <UtilityText size="sm" css={{ color: '$onSurfaceTextCritical' }}>
            {node.error}
          </UtilityText>
        )}
      </StackLayout>

      {node.childNodes.length > 0 && (
        <Box css={{ marginInlineStart: '$oneAndHalf' }}>
          <Collapsible open={open} onOpenChange={setOpen}>
            <Box
              as={CollapsibleTrigger}
              css={{
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '$button',
                width: '100%',
                cursor: 'pointer',
                '&[data-state="open"] .collapsible-icon': {
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <StackLayout
                orientation="horizontal"
                spacing="$half"
                verticalAlignment="center"
              >
                <Icon
                  icon="interfaceChevronRight"
                  size="$threeQuarters"
                  className="collapsible-icon"
                />
                <UtilityText emphasis="subtle" css={{ typography: '$bodyOne' }}>
                  {formatMessage(
                    { id: 'DEEP_PUBLISH_CHILD_ENTITIES_COUNT' },
                    { count: node.childNodes.length },
                  )}
                </UtilityText>
              </StackLayout>
            </Box>
            <CollapsibleContent>
              <Box css={{ paddingX: '$oneAndHalf', paddingBlockEnd: '$one' }}>
                {node.childNodes.map((childNode, index) => {
                  const key = `${childNode.id}-${index}`;
                  return (
                    <DeepPublishTreeItem
                      key={key}
                      node={childNode}
                      duplicateIndicatorMessageId={duplicateIndicatorMessageId}
                    />
                  );
                })}
              </Box>
            </CollapsibleContent>
          </Collapsible>
        </Box>
      )}
    </StackLayout>
  );
};

export const DeepPublishConfirmationModal = ({
  isOpen,
  isLoading,
  rootNode,
  errorMessage = null,
  hasFailures = false,
  publishableCount,
  publishDangerously = false,
  environmentName,
  onConfirm,
  onClose,
}: DeepPublishConfirmationModalProps) => {
  const { formatMessage } = useIntl();
  const [dangerouslyConfirmed, setDangerouslyConfirmed] = React.useState(false);
  const isReady = !isLoading && rootNode;
  const hasErrors = Boolean(errorMessage) || hasFailures;
  const needsDangerousConfirmation =
    publishDangerously && !dangerouslyConfirmed;
  const isConfirmDisabled = hasErrors || !isReady || needsDangerousConfirmation;

  React.useEffect(() => {
    if (!isOpen) {
      setDangerouslyConfirmed(false);
    }
  }, [isOpen]);

  return (
    <FullscreenConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      titleId="DEEP_PUBLISH_MODAL_TITLE"
      descriptionId="DEEP_PUBLISH_MODAL_DESCRIPTION"
      isLoading={isLoading}
      loadingTextId="DEEP_PUBLISH_MODAL_FETCHING_TEXT"
      confirmLabel={formatMessage(
        { id: 'DEEP_PUBLISH_CONFIRM_BUTTON' },
        { count: publishableCount },
      )}
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
      footerContent={
        publishDangerously && isReady ? (
          <StackLayout horizontalAlignment="end">
            <StackLayout
              orientation="horizontal"
              spacing="$quarter"
              verticalAlignment="center"
            >
              <Icon icon="tinyAlert" size="$one" tint="$warningIcon" />
              <UtilityText>
                {formatMessage(
                  { id: 'DEEP_PUBLISH_DANGEROUSLY_CHECKBOX_WARNING' },
                  { environment: environmentName },
                )}
              </UtilityText>
            </StackLayout>
            <Checkbox
              id="deep-publish-dangerously-confirmation"
              name="deep-publish-dangerously-confirmation"
              label={
                <UtilityText>
                  {formatMessage({
                    id: 'DEEP_PUBLISH_DANGEROUSLY_CHECKBOX_ACKNOWLEDGE',
                  })}
                </UtilityText>
              }
              checked={dangerouslyConfirmed}
              onChange={() => setDangerouslyConfirmed((previous) => !previous)}
            />
          </StackLayout>
        ) : undefined
      }
    >
      {hasFailures && (
        <StatusBanner status="error" css={{ flex: 'none' }}>
          <UtilityText as="p">
            {formatMessage({
              id: 'DEEP_PUBLISH_DISCOVERY_FAILURE_ERROR',
            })}
          </UtilityText>
        </StatusBanner>
      )}
      {errorMessage && (
        <ParagraphText css={{ color: '$onSurfaceTextCritical' }}>
          {errorMessage}
        </ParagraphText>
      )}
      {isReady && rootNode && (
        <Box>
          <DeepPublishTreeItem node={rootNode} />
        </Box>
      )}
    </FullscreenConfirmationModal>
  );
};
